/**
 * Single source of truth for rental slot metadata on the admin side. A show's
 * rental slot template lives in `marketing.rentalSlots` (see backend RentalSlot).
 * Timeslots and ticket options reference a slot by its stable `key`; ordering and
 * labels are derived from the template instead of the old hardcoded am/pm/full_day
 * maps.
 */
import i18n from 'i18next';

/**
 * Normalized, ordered slot template for a show. Empty array when the show has no
 * template (non-rental).
 * @returns {{key:string,label:string,labelI18n:object|null,timeOfDay:string,durationMinutes:number|null,isFullDay:boolean}[]}
 */
export function getSlotTemplate(show) {
    const slots = show?.marketing?.rentalSlots;
    if (!Array.isArray(slots)) return [];
    return slots
        .filter((s) => s && s.key)
        .map((s) => ({
            key: s.key,
            label: s.label || '',
            labelI18n: s.labelI18n || null,
            timeOfDay: s.timeOfDay || '',
            durationMinutes: s.durationMinutes ?? null,
            isFullDay: !!s.isFullDay,
        }));
}

/** Sort index for a slot key (template order); Infinity when not in the template. */
export function slotOrderIndex(template, slotKey) {
    const idx = (template || []).findIndex((s) => s.key === slotKey);
    return idx === -1 ? Infinity : idx;
}

/**
 * Display label for a timeslot (or a template slot). Resolution order:
 * localized template label -> template label -> the slot key itself -> formatted
 * start time. Resilient to a slot removed from the template after timeslots
 * referencing it already exist.
 */
export function resolveSlotLabel(template, timeslotOrSlot) {
    const slotKey = timeslotOrSlot?.slotKey ?? timeslotOrSlot?.key ?? null;
    const entry = slotKey ? (template || []).find((s) => s.key === slotKey) : null;
    const language = i18n.resolvedLanguage || i18n.language;
    const localizedLabel =
        entry?.labelI18n?.[language] || entry?.labelI18n?.[language?.split('-')[0]];
    if (localizedLabel) return localizedLabel;
    if (entry?.label) return entry.label;
    if (slotKey) return slotKey;
    const start = timeslotOrSlot?.startTime ?? timeslotOrSlot?.start_time ?? null;
    if (start) return new Date(start).toLocaleString();
    return '';
}
